import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, Check, X, Car, Zap, Warehouse, Briefcase, Bus, Home, Building2,
  Phone, MessageCircle, Calculator as CalcIcon,
} from "lucide-react";
import { Reveal, SectionHead, Field, CountUp } from "../components/ui-bits";
import { useApp } from "../context/AppContext";
import { submitB2B, formatApiError, waLink } from "../lib/api";
import { IMAGES } from "../config/images";
import { B2B_SEGMENTS, B2B_SERVICES, B2B_COMPARISON, B2B_TYPES } from "../config/defaults";

const SEG_ICONS = { car: Car, zap: Zap, warehouse: Warehouse, briefcase: Briefcase, bus: Bus, home: Home, building: Building2 };

export default function ForBusinessPage() {
  const formRef = useRef(null);
  const goForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="pt-[68px] pb-28 lg:pb-0" data-testid="for-business-page">
      <B2BHero onCta={goForm} />
      <Segments />
      <Services />
      <ImpactTable />
      <Calculator />
      <FinalForm refEl={formRef} />
      <B2BStickyBar onCta={goForm} />
    </main>
  );
}

function B2BHero({ onCta }) {
  return (
    <header className="relative overflow-hidden" data-testid="b2b-hero">
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${IMAGES.b2bIndia})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(19,8,38,0.82) 0%, rgba(19,8,38,0.94) 100%)" }} />
      <div className="dot-grid pointer-events-none absolute inset-0" />
      <div className="container-x relative flex max-w-3xl flex-col gap-5 py-16 lg:py-24">
        <Reveal><span className="eyebrow">Carza For Business</span></Reveal>
        <Reveal delay={0.06}>
          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-paper sm:text-5xl lg:text-6xl" data-testid="b2b-headline">
            Reliable Car Care for <span className="text-carza-lite text-glow">Businesses That Move.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="max-w-2xl text-base leading-relaxed text-mist sm:text-lg" data-testid="b2b-subheading">
            Standardized, convenient and water-efficient vehicle-care solutions for dealerships, fleets, companies, societies and mobility businesses.
          </p>
        </Reveal>
        <Reveal delay={0.2} className="mt-3 flex flex-wrap gap-4">
          <button onClick={onCta} className="btn btn-primary px-7 py-4 text-[15px]" data-testid="b2b-hero-proposal-cta">
            Request a B2B Proposal <ArrowRight size={16} />
          </button>
          <a href={waLink("917011663967", "Hi CARZA, I'd like to discuss a B2B car-care program.")} target="_blank" rel="noopener noreferrer" className="btn btn-outline px-7 py-4 text-[15px]" data-testid="b2b-hero-talk-cta">
            <MessageCircle size={16} className="text-carza-lite" /> Talk to Our Business Team
          </a>
        </Reveal>
      </div>
    </header>
  );
}

function Segments() {
  return (
    <section className="relative py-20 lg:py-24" data-testid="b2b-segments">
      <div className="container-x">
        <SectionHead eyebrow="Who We Serve" title={<>One Network. Every Business Vehicle.</>} sub="Programs shaped around how your business uses its vehicles." />
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:grid-cols-4">
          {B2B_SEGMENTS.map((s, i) => {
            const Icon = SEG_ICONS[s.icon] || Building2;
            const wide = i >= 3;
            return (
              <Reveal key={s.title} delay={(i % 4) * 0.06} className={wide ? "lg:col-span-1" : ""}>
                <div className="card-surface flex h-full flex-col gap-4 rounded-3xl p-6" data-testid={`b2b-segment-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-carza-dim text-carza-lite"><Icon size={19} /></span>
                  <div>
                    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-paper">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
          <Reveal delay={0.24} className="lg:col-span-1">
            <div className="flex h-full flex-col items-start justify-center gap-3 rounded-3xl border border-dashed border-white/15 p-6" data-testid="b2b-segment-other">
              <p className="text-sm text-mist">Another fleet or use case?</p>
              <p className="font-display text-[15px] font-semibold text-carza-lite">We design custom programs.</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="relative py-20 lg:py-24" data-testid="b2b-services">
      <div className="container-x">
        <SectionHead eyebrow="B2B Services" title={<>What Can CARZA Do For Your Business?</>} sub="Six program types, each with a clear owner, scope and benefit." />
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {B2B_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.06}>
              <div className="card-surface flex h-full flex-col gap-4 rounded-3xl p-6" data-testid={`b2b-service-${i}`}>
                <span className="font-mono text-[11px] font-semibold tracking-widest text-carza-lite">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-lg font-semibold text-paper">{s.title}</h3>
                <div className="flex flex-1 flex-col gap-3 text-sm leading-relaxed">
                  <p className="text-mist"><span className="font-semibold text-paper/90">What it is —</span> {s.what}</p>
                  <p className="text-mist"><span className="font-semibold text-paper/90">Who it's for —</span> {s.who}</p>
                  <p className="text-mist"><span className="font-semibold text-carza-lite">Business benefit —</span> {s.benefit}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactTable() {
  return (
    <section className="relative py-20 lg:py-24" data-testid="b2b-impact">
      <div className="container-x">
        <SectionHead eyebrow="Cost & Benefit" title={<>Understand the Business Impact.</>} sub="How a CARZA program compares to managing traditional, unorganized car care." align="center" />
        <Reveal delay={0.1} className="mt-12">
          <div className="card-surface overflow-hidden rounded-3xl" data-testid="b2b-comparison-table">
            <div className="hidden grid-cols-12 border-b border-white/10 bg-white/[0.03] px-6 py-4 font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2 lg:grid">
              <span className="col-span-3">Factor</span>
              <span className="col-span-4 text-mist">Traditional model</span>
              <span className="col-span-5 text-carza-lite">With Carza</span>
            </div>
            {B2B_COMPARISON.map((row, i) => (
              <div key={row.factor} className={`grid grid-cols-1 gap-3 px-6 py-5 lg:grid-cols-12 lg:gap-6 ${i < B2B_COMPARISON.length - 1 ? "border-b border-white/5" : ""}`} data-testid={`b2b-impact-row-${i}`}>
                <span className="col-span-3 text-[13px] font-semibold text-paper">{row.factor}</span>
                <span className="col-span-4 flex items-start gap-2.5 text-[13px] leading-relaxed text-mist">
                  <X size={13} className="mt-0.5 shrink-0 text-[#8a8399]" /> {row.traditional}
                </span>
                <span className="col-span-5 flex items-start gap-2.5 text-[13px] font-medium leading-relaxed text-paper/90">
                  <span className="mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-carza text-white" style={{ width: 18, height: 18 }}><Check size={10} /></span>
                  {row.carza}
                </span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-6 max-w-xl text-center text-[11px] leading-relaxed text-mist-2">
            Comparisons describe operating characteristics, not verified monetary savings. Program economics are shared in your tailored proposal.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Calculator() {
  const { config } = useApp();
  const [vehicles, setVehicles] = useState(25);
  const [perMonth, setPerMonth] = useState(8);
  const [price, setPrice] = useState(config.b2b_program?.default_service_price ?? 249);
  const monthly = Math.round((Number(vehicles) || 0) * (Number(perMonth) || 0) * (Number(price) || 0));

  return (
    <section className="relative pb-20 lg:pb-24" data-testid="b2b-calculator-section">
      <div className="container-x">
        <Reveal>
          <div className="mx-auto max-w-4xl rounded-[2rem] border border-carza/25 p-7 sm:p-10" style={{ background: "radial-gradient(140% 160% at 20% 0%, rgba(124,77,255,0.16) 0%, rgba(26,14,51,1) 70%)" }}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza-lite"><CalcIcon size={18} /></span>
              <div>
                <h3 className="font-display text-xl font-bold text-paper">Estimate Your Program</h3>
                <p className="text-xs text-mist">A quick sizing tool — indicative only.</p>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 items-end gap-5 sm:grid-cols-3" data-testid="b2b-calculator">
              <Field label="Number of Vehicles">
                <input className="field" type="number" min="1" value={vehicles} onChange={(e) => setVehicles(e.target.value)} data-testid="calc-vehicles-input" />
              </Field>
              <Field label="Services per Month">
                <input className="field" type="number" min="1" value={perMonth} onChange={(e) => setPerMonth(e.target.value)} data-testid="calc-services-input" />
              </Field>
              <Field label="Service Price (₹)">
                <input className="field" type="number" min="1" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="calc-price-input" />
              </Field>
            </div>
            <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl border border-carza/30 bg-carza-dim px-6 py-5 sm:flex-row" data-testid="calc-result">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Estimated monthly program value</p>
              <p className="font-display text-3xl font-bold text-carza-lite sm:text-4xl" data-testid="calc-result-value">
                ₹<CountUp key={monthly} to={monthly} duration={0.7} />
              </p>
            </div>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-mist-2">{config.b2b_program?.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const FinalForm = ({ refEl }) => {
  const [data, setData] = useState({ name: "", company: "", designation: "", business_type: B2B_TYPES[0], vehicles: "", city: "", phone: "", email: "", message: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitB2B(data);
      setDone(true);
      toast.success("Proposal request received. Our B2B team will reach out shortly.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={refEl} className="relative py-16 lg:py-24" data-testid="b2b-form-section">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-[2rem] border border-carza/30 p-7 sm:p-12" style={{ background: "radial-gradient(120% 160% at 50% 0%, rgba(124,77,255,0.24) 0%, rgba(19,8,38,1) 62%)" }}>
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="eyebrow">Get Started</span>
              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-paper sm:text-4xl">Let's Build Your Car-Care Program.</h2>
              <p className="mt-4 text-sm leading-relaxed text-mist">Tell us about your vehicles and we'll design a program with clear scope, schedule and pricing.</p>
              <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="b2b-direct-contact">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Prefer to speak directly?</p>
                <p className="mt-2 text-sm text-paper">Talk to our Business Team</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a href="tel:+917011663967" className="btn btn-outline px-5 py-2.5 text-[13px]" data-testid="b2b-call-btn"><Phone size={14} className="text-carza-lite" /> +91 70116 63967</a>
                  <a href={waLink("917011663967", "Hi CARZA, I'd like to discuss a B2B car-care program.")} target="_blank" rel="noopener noreferrer" className="btn btn-outline px-5 py-2.5 text-[13px]" data-testid="b2b-whatsapp-btn"><MessageCircle size={14} className="text-carza-lite" /> WhatsApp</a>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              {done ? (
                <div className="flex flex-col items-center gap-4 py-12 text-center" data-testid="b2b-form-success">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-carza-dim text-carza-lite"><Check size={26} /></span>
                  <h3 className="font-display text-2xl font-bold text-paper">Request received</h3>
                  <p className="max-w-sm text-sm text-mist">Thank you, {data.name.split(" ")[0]}. Our B2B team will contact you within 1–2 working days with a tailored proposal.</p>
                  <button onClick={() => { setDone(false); setData({ ...data, name: "", company: "", message: "" }); }} className="btn btn-outline mt-2 px-6 py-3">Submit another request</button>
                </div>
              ) : (
                <form onSubmit={submit} className="glass flex flex-col gap-4 rounded-3xl p-6 sm:p-8" data-testid="b2b-form">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Name"><input className="field" value={data.name} onChange={set("name")} required placeholder="Full name" data-testid="b2b-name-input" /></Field>
                    <Field label="Company"><input className="field" value={data.company} onChange={set("company")} required placeholder="Company name" data-testid="b2b-company-input" /></Field>
                    <Field label="Designation" required={false}><input className="field" value={data.designation} onChange={set("designation")} placeholder="e.g. Operations Head" data-testid="b2b-designation-input" /></Field>
                    <Field label="Business Type">
                      <select className="field appearance-none" value={data.business_type} onChange={set("business_type")} data-testid="b2b-type-select">
                        {B2B_TYPES.map((t) => <option key={t} value={t} className="bg-graphite">{t}</option>)}
                      </select>
                    </Field>
                    <Field label="Number of Vehicles"><input className="field" value={data.vehicles} onChange={set("vehicles")} required inputMode="numeric" placeholder="e.g. 40" data-testid="b2b-vehicles-input" /></Field>
                    <Field label="City"><input className="field" value={data.city} onChange={set("city")} required placeholder="City" data-testid="b2b-city-input" /></Field>
                    <Field label="Phone"><input className="field" value={data.phone} onChange={set("phone")} required inputMode="tel" placeholder="Phone number" data-testid="b2b-phone-input" /></Field>
                    <Field label="Email"><input className="field" type="email" value={data.email} onChange={set("email")} required placeholder="Work email" data-testid="b2b-email-input" /></Field>
                  </div>
                  <Field label="Requirement" required={false}><textarea className="field min-h-[90px] resize-none" value={data.message} onChange={set("message")} placeholder="Tell us about your fleet, locations and schedule…" data-testid="b2b-message-input" /></Field>
                  <input type="text" name="company_website" value={data.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} aria-hidden="true" autoComplete="off" />
                  {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="b2b-error">{error}</p>}
                  <button type="submit" disabled={loading} className="btn btn-primary w-full py-4 disabled:opacity-50" data-testid="b2b-submit-btn">
                    {loading ? "Submitting…" : <>Request a B2B Proposal <ArrowUpRight size={15} /></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function B2BStickyBar({ onCta }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-white/10 bg-ink/85 px-4 py-3 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
      data-testid="b2b-sticky-bar"
    >
      <Building2 size={18} className="ml-2 shrink-0 text-carza-lite" />
      <button onClick={onCta} className="btn btn-primary h-12 flex-1 text-[15px]" data-testid="b2b-sticky-cta">
        Request Proposal
      </button>
    </div>
  );
}
